import React, { useState, useEffect } from 'react';
import { TreePine, Camera, Check, Filter } from 'lucide-react';
import { COMPANY_INFO, FALLBACK_GALLERIES } from '../data';
import GallerySection from '../components/GallerySection';

export default function GalleryPage() {
  const [galleries, setGalleries] = useState([]);
  const [activeFilter, setActiveFilter] = useState('alle');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set Document Metadata
    document.title = `Billedgalleri | ${COMPANY_INFO.name}`;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', "Se billeder af vores maskinskovning, skovservice, naturpleje, flishugning, udkørsel samt stub- og rodfræsning i Nordjylland.");
    }
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', "https://www.vesthimmerlands-maskinskovning.dk/galleri");

    setLoading(true);
    // Fetch Galleries from CMS
    fetch('https://api.dev.auxo.dk/public/content/gallery')
      .then(res => res.json())
      .then(data => {
        if (data && data.length > 0) {
          setGalleries(data);
        } else {
          loadFallback();
        }
      })
      .catch(err => {
        console.warn("Kunne ikke hente gallerier fra CMS, bruger fallback:", err);
        loadFallback();
      })
      .finally(() => {
        setLoading(false);
      });

    function loadFallback() {
      const fallbackList = Object.keys(FALLBACK_GALLERIES).map(key => {
        let title = key.charAt(0).toUpperCase() + key.slice(1);
        if (key === 'stub-og-rodfraesning') title = 'Stub- og Rodfræsning';
        if (key === 'vognmand') title = 'Vognmandskørsel';
        if (key === 'udkoersel') title = 'Udkørsel';
        if (key === 'maskinskovning') title = 'Maskinskovning og udkørsel';
        return {
          gallery_slug: key,
          title: { da: title },
          images: FALLBACK_GALLERIES[key]
        };
      });
      setGalleries(fallbackList);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-stone-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-stone-400 text-sm font-semibold uppercase tracking-wider">Indlæser gallerier...</p>
        </div>
      </div>
    );
  }

  // Filter options
  const filterOptions = [
    { id: 'alle', label: 'Alle billeder' },
    ...galleries.map(g => ({ id: g.gallery_slug, label: g.title?.da || g.title }))
  ];

  // Get active galleries to render
  const filteredGalleries = activeFilter === 'alle' 
    ? galleries 
    : galleries.filter(g => g.gallery_slug === activeFilter);

  return (
    <div className="bg-stone-950 min-h-screen">
      {/* Page Hero */}
      <section className="relative h-[35vh] min-h-[250px] flex items-center justify-center overflow-hidden bg-stone-950 border-b border-stone-900">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/fc64567f-b2fe-44de-b030-fbc580ebe27a.jpg" 
            alt="Billedgalleri" 
            className="w-full h-full object-cover object-center opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/60 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-swamp-300 mb-2 block">
            Dokumentation af vores arbejde
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white uppercase tracking-tight leading-none">
            Billedgalleri
          </h1>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-12">
        {/* Filter Controls */}
        <div className="bg-stone-900 border border-stone-800 p-4 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-4 shadow-lg">
          <div className="flex items-center gap-2.5 text-stone-200 text-sm font-bold">
            <Filter className="w-4 h-4 text-swamp-300" />
            <span>Filtrer efter kategori:</span>
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            {filterOptions.map(option => (
              <button
                key={option.id}
                onClick={() => setActiveFilter(option.id)}
                className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold tracking-wide transition-all cursor-pointer ${
                  activeFilter === option.id
                    ? 'bg-swamp-900 text-white border border-swamp-600 shadow-md shadow-black/40 scale-[1.03]'
                    : 'bg-stone-950 border border-stone-800 text-stone-300 hover:text-white hover:border-swamp-500'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Render Galleries */}
        <div className="space-y-16">
          {filteredGalleries.length === 0 ? (
            <div className="text-center py-16 bg-stone-900/40 border border-stone-900 rounded-2xl">
              <Camera className="w-12 h-12 text-stone-600 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white uppercase tracking-tight">Ingen billeder fundet</h3>
              <p className="text-stone-400 text-sm mt-1">Der er i øjeblikket ingen billeder i denne kategori.</p>
            </div>
          ) : (
            filteredGalleries.map(gallery => (
              <div key={gallery.gallery_slug} className="bg-stone-900/20 border border-stone-900/60 p-6 sm:p-8 rounded-2xl">
                <GallerySection 
                  images={gallery.images} 
                  title={gallery.title?.da || gallery.title} 
                />
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
